/**
 * HangarMax → PlaneMAX geometric transforms.
 *
 * ============================================================================
 * CALIBRATION SURFACE
 * ============================================================================
 * Both codebases hide sign flips inside their render paths, so the rotation
 * sign and the door wall map below are DERIVED, not observed. Every one of them
 * is defined exactly once in this file and marked `CALIBRATION:` — if the
 * fixture layout imports mirrored, flip it here and nowhere else.
 *
 * Derivations (see the plan's cross-system facts):
 *  - Both scenes use +X east, +Y up, +Z south.
 *  - HangarMax applies `group.rotation.y = -(bearing * PI/180)`.
 *  - PlaneMAX applies `group.rotation.y = +(rotation * PI/180)`
 *    (areas: `useCreateArea` stores `bearing_deg` straight into `areas.rotation`;
 *     aircraft: `AircraftLoaderService` adds `rotation * PI/180` on Y).
 *  => pmRotation = -hmBearing  (mod 360)
 *  - HangarMax renders its `left` wall at +X and `right` at -X (deliberate
 *    swap), so an HM `left` door lands on the PlaneMAX `right` wall.
 * ============================================================================
 */

import { METERS_TO_FEET } from '@/constants/units'
import { lngLatToSceneFeet, sceneFeetToLngLat } from './mercator'
import type { HmAnchor, HmDoorWall } from './types'
import type { DoorWall } from '@/lib/templates/types'

/** HangarMax adds walls outside the stated footprint at a fixed thickness. */
export const HM_WALL_THICKNESS_M = 0.1524

export function norm360(deg: number): number {
  return ((deg % 360) + 360) % 360
}

export function metersToFeet(m: number): number {
  return m * METERS_TO_FEET
}

// ----------------------------------------------------------------------------
// Rotations
// ----------------------------------------------------------------------------

/**
 * CALIBRATION: PlaneMAX `areas.rotation` (deg) from a HangarMax building bearing.
 * Both are zero when the door wall faces south.
 */
export function buildingRotationFromHmBearing(bearingDeg: number): number {
  return norm360(360 - bearingDeg)
}

/**
 * CALIBRATION: PlaneMAX scene rotation (deg) from a HangarMax item rotation
 * (deg clockwise from north). Sanity check: aircraft compass heading is
 * `180 - sceneRotation` (see `demo/masters-planner/engine/heading.ts`).
 */
export function itemRotationFromHmRotation(rotationDeg: number): number {
  return norm360(360 - rotationDeg)
}

/**
 * Unit-local obstacle rotation for an item inside a building.
 *
 * The obstacle renderer applies `rotation.y = -(obstacle.rotation)` INSIDE the
 * building group, which itself carries `+areaRotation`. Solving
 * `areaRotation - obstacleRotation = -hmItemRotation` gives the value below;
 * an item aligned with its building (hmRotation == hmBearing) correctly yields 0.
 */
export function obstacleRotationFromHm(
  hmItemRotationDeg: number,
  areaRotationDeg: number
): number {
  return norm360(areaRotationDeg + hmItemRotationDeg)
}

// ----------------------------------------------------------------------------
// Positions
// ----------------------------------------------------------------------------

/**
 * Offset of the HangarMax scene anchor from the PlaneMAX location centre,
 * in feet. Computed ONCE per import; per-object positions are then plain
 * additions, avoiding a GPS round-trip (and its rounding) for every object.
 */
export function anchorOffsetFeet(
  anchor: HmAnchor,
  locationCenter: { lat: number; lng: number }
): [number, number, number] {
  return lngLatToSceneFeet(
    { lat: anchor.latitude, lng: anchor.longitude },
    locationCenter
  )
}

/** HangarMax scene metres (+X east, +Z south) → PlaneMAX scene feet. */
export function hmSceneToPmSceneFeet(
  anchorOffsetFt: [number, number, number],
  hm: { x: number; y?: number; z: number }
): [number, number, number] {
  return [
    anchorOffsetFt[0] + metersToFeet(hm.x),
    metersToFeet(hm.y ?? 0),
    anchorOffsetFt[2] + metersToFeet(hm.z),
  ]
}

/** PlaneMAX scene feet → lat/lng (for `areas.latitude/longitude`). */
export function sceneFeetToLatLng(
  position: [number, number, number],
  locationCenter: { lat: number; lng: number }
): { lat: number; lng: number } {
  return sceneFeetToLngLat(position, locationCenter)
}

// ----------------------------------------------------------------------------
// Doors
// ----------------------------------------------------------------------------

/**
 * CALIBRATION: HangarMax wall → PlaneMAX wall.
 * left/right swap because HangarMax renders `left` at +X and `right` at -X.
 */
export const HM_TO_PM_DOOR_WALL: Record<HmDoorWall, DoorWall> = {
  front: 'front',
  back: 'back',
  left: 'right',
  right: 'left',
}

/**
 * CALIBRATION: whether remapping a wall also reverses the along-wall direction.
 *
 * Mirroring the left/right walls could reverse which end `offset_ft` counts
 * from. Set to true if the calibration fixture shows door offsets mirrored on
 * the side walls only.
 */
export const DOOR_OFFSET_FLIPS_ON_SWAPPED_WALL = false

/**
 * Convert a HangarMax centre-relative door offset to the PlaneMAX
 * leading-edge offset used by `DoorSpec.offset_ft`.
 *
 * HangarMax stores `offset_m` relative to the wall CENTRE and flips its sign on
 * the left/right walls; PlaneMAX stores the distance from the start of the wall
 * to the door's leading edge. Working from wall centres (rather than converting
 * HangarMax's own edge offset) keeps the door in the same place even though
 * PlaneMAX wall lengths are the exterior dimensions.
 *
 * The result is clamped into the wall, matching HangarMax's own clamping.
 */
export function doorOffsetFeet(params: {
  hmWall: HmDoorWall
  /** Signed metres from the wall centre (HangarMax convention). */
  hmOffsetM: number
  /** Door width in feet. */
  doorWidthFt: number
  /** Length of the PlaneMAX wall the door lands on, in feet. */
  wallLengthFt: number
}): number {
  const { hmWall, hmOffsetM, doorWidthFt, wallLengthFt } = params

  // HangarMax's own sign convention for the side walls.
  let signed = hmWall === 'left' || hmWall === 'right' ? -hmOffsetM : hmOffsetM

  if (DOOR_OFFSET_FLIPS_ON_SWAPPED_WALL && HM_TO_PM_DOOR_WALL[hmWall] !== hmWall) {
    signed = -signed
  }

  const leadingEdge = wallLengthFt / 2 + metersToFeet(signed) - doorWidthFt / 2
  const maxOffset = Math.max(0, wallLengthFt - doorWidthFt)

  return Math.min(Math.max(leadingEdge, 0), maxOffset)
}

/** Length of the PlaneMAX wall a door sits on, in feet. */
export function wallLengthFeet(
  pmWall: DoorWall,
  widthFt: number,
  depthFt: number
): number {
  return pmWall === 'front' || pmWall === 'back' ? widthFt : depthFt
}

// ----------------------------------------------------------------------------
// Building-local frames (host resolution)
// ----------------------------------------------------------------------------

export interface AreaFrame {
  /** Scene feet, [east, up, south]. */
  centerFt: [number, number, number]
  /** PlaneMAX degrees. */
  rotationDeg: number
  widthFt: number
  depthFt: number
}

/**
 * Scene point → building-local feet, undoing the Three.js Y rotation PlaneMAX
 * applies to the area group.
 *
 * Forward (see `lib/utils/unit-position.ts`):
 *   worldX = localX*cos + localZ*sin
 *   worldZ = -localX*sin + localZ*cos
 * The inverse is the transpose.
 */
export function sceneToAreaLocalFeet(
  point: [number, number, number],
  area: AreaFrame
): { x: number; z: number } {
  const dx = point[0] - area.centerFt[0]
  const dz = point[2] - area.centerFt[2]
  const rad = (area.rotationDeg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)

  return {
    x: dx * cos - dz * sin,
    z: dx * sin + dz * cos,
  }
}

/** True when a building-local point falls inside the area footprint. */
export function isInsideAreaFootprint(
  local: { x: number; z: number },
  area: AreaFrame,
  toleranceFt = 0
): boolean {
  return (
    Math.abs(local.x) <= area.widthFt / 2 + toleranceFt &&
    Math.abs(local.z) <= area.depthFt / 2 + toleranceFt
  )
}

/**
 * Building-local point → the unit-local (position_x, position_y) an obstacle row
 * stores.
 *
 * `ObstacleRenderer` places an obstacle at
 *   x = (unit.offset_x - width/2 + position_x)
 *   z = -((unit.offset_y - centerZShift) + position_y)
 * so `position_x` is measured from the unit's LEFT EDGE while `position_y` is
 * measured from the unit's offset_y line (north-positive). This inverts that.
 */
export function areaLocalToObstacleUnitLocal(
  local: { x: number; z: number },
  unit: { offsetX: number; offsetY: number },
  areaWidthFt: number,
  centerZShiftFt = 0
): { positionX: number; positionY: number } {
  return {
    positionX: local.x - (unit.offsetX - areaWidthFt / 2),
    positionY: -local.z - (unit.offsetY - centerZShiftFt),
  }
}
